import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWebSocket, useWsEvent } from '@/hooks/use-websocket';
import { getActiveTimeEntries, getOcrHealth, getPaidNotCompletedBookings, getPendingReceipts, getReceiptReviewAttentionItems, } from '@/services/pos-api';
import { toDateStringInTz, VENUE_TIMEZONE } from '@/lib/timezone';
const MAX_STREAM_EVENTS = 25;
function formatCurrency(amount) {
    return `$${Number(amount).toFixed(2)}`;
}
function formatVenueDate(date) {
    return new Date(date).toLocaleDateString('en-CA', {
        month: 'short',
        day: 'numeric',
        timeZone: VENUE_TIMEZONE,
    });
}
function formatVenueTime(date) {
    return new Date(date).toLocaleTimeString('en-CA', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: VENUE_TIMEZONE,
    });
}
function describeOcrHealth(health) {
    if (!health.reachable)
        return health.error || 'OCR Pi unreachable';
    if (health.modelLoaded === false)
        return 'OCR Pi reachable; model is still loading';
    return health.responseTimeMs != null
        ? `OCR Pi online · ${health.responseTimeMs}ms`
        : 'OCR Pi online';
}
function isPriorVenueDay(date, selectedDate) {
    return toDateStringInTz(date, VENUE_TIMEZONE) < selectedDate;
}
function mergeReceiptReviewItems(selectedDayItems, backlogItems) {
    const seen = new Set();
    return [...selectedDayItems, ...backlogItems].filter((item) => {
        if (seen.has(item.paymentId))
            return false;
        seen.add(item.paymentId);
        return true;
    });
}
function reviewStatusLabel(status) {
    if (status === 'VERIFIED')
        return 'manually verified';
    if (status === 'REJECTED')
        return 'manually rejected';
    return 'manual review cleared';
}
function buildReceiptFocusHref({ bookingId, paymentId, seatIndex, status, reason, }) {
    const params = new URLSearchParams({
        focus: 'receipt',
        paymentId,
    });
    if (seatIndex)
        params.set('seat', String(seatIndex));
    if (status)
        params.set('status', status);
    if (reason)
        params.set('reason', reason);
    return `/pos/booking/${bookingId}?${params.toString()}`;
}
function streamEventFromWs(evt) {
    const timestamp = new Date(evt.timestamp);
    if (Number.isNaN(timestamp.getTime()))
        return null;
    switch (evt.type) {
        case 'receipt.uploaded': {
            const payload = evt.payload;
            return {
                id: `receipt-uploaded-${payload.paymentId}-${evt.timestamp}`,
                timestamp,
                type: 'ReceiptUpload',
                primary: payload.countDelta === 0 ? 'Receipt replaced' : 'Receipt uploaded',
                secondary: payload.bookingId ? `Booking ${payload.bookingId.slice(0, 8)}` : undefined,
                meta: payload.paymentId,
                linkHref: payload.bookingId ? `/pos/booking/${payload.bookingId}` : '/admin/receipt-analysis',
            };
        }
        case 'receipt.deleted': {
            const payload = evt.payload;
            return {
                id: `receipt-deleted-${payload.paymentId}-${evt.timestamp}`,
                timestamp,
                type: 'ReceiptUpload',
                primary: 'Receipt removed',
                secondary: payload.bookingId ? `Booking ${payload.bookingId.slice(0, 8)}` : undefined,
                meta: payload.paymentId,
                linkHref: payload.bookingId ? `/pos/booking/${payload.bookingId}` : '/admin/receipt-analysis',
            };
        }
        case 'receipt.analysis_complete': {
            const payload = evt.payload;
            return {
                id: `receipt-analysis-${payload.paymentId}-${evt.timestamp}`,
                timestamp,
                type: 'ReceiptReview',
                primary: `OCR ${payload.matchStatus || 'analysis complete'}`,
                secondary: payload.bookingId ? `Booking ${payload.bookingId.slice(0, 8)}` : undefined,
                meta: payload.paymentId,
                linkHref: payload.bookingId ? `/pos/booking/${payload.bookingId}` : '/admin/receipt-analysis',
            };
        }
        case 'receipt.analysis_progress': {
            const payload = evt.payload;
            if (!['failed', 'completed'].includes(payload.stage))
                return null;
            return {
                id: `receipt-analysis-progress-${payload.analysisRunId}-${payload.stage}-${evt.timestamp}`,
                timestamp,
                type: 'OcrAnalyzer',
                level: payload.level,
                primary: payload.message,
                secondary: payload.bookingId ? `Booking ${payload.bookingId.slice(0, 8)}` : undefined,
                meta: [
                    payload.paymentId,
                    payload.detail,
                    payload.elapsedMs != null ? `${payload.elapsedMs}ms` : undefined,
                ].filter(Boolean).join(' · '),
                linkHref: payload.bookingId ? `/pos/booking/${payload.bookingId}` : '/admin/receipt-analysis',
            };
        }
        case 'receipt.manual_review': {
            const payload = evt.payload;
            return {
                id: `receipt-review-${payload.paymentId}-${evt.timestamp}`,
                timestamp,
                type: 'ReceiptReview',
                primary: reviewStatusLabel(payload.manualReviewStatus),
                secondary: payload.bookingId ? `Booking ${payload.bookingId.slice(0, 8)}` : undefined,
                meta: [payload.paymentId, payload.managerEmployeeName].filter(Boolean).join(' · '),
                linkHref: payload.bookingId ? `/pos/booking/${payload.bookingId}` : '/admin/receipt-analysis',
            };
        }
        case 'ocr.pi_health_changed': {
            const payload = evt.payload;
            return {
                id: `ocr-health-${evt.timestamp}`,
                timestamp,
                type: 'OcrHealth',
                primary: describeOcrHealth(payload),
                meta: payload.ocrServiceUrl,
                linkHref: '/admin/receipt-analysis',
            };
        }
        case 'timeclock.clocked_in':
        case 'timeclock.clocked_out':
        case 'timeclock.edited': {
            const payload = evt.payload;
            return {
                id: `timeclock-${payload.change}-${payload.entryId || payload.employeeId || evt.timestamp}`,
                timestamp,
                type: 'StatusChange',
                primary: `Timeclock ${payload.change.replace('_', ' ')}`,
                meta: payload.employeeId,
                linkHref: '/pos/time-management',
            };
        }
        default:
            return null;
    }
}
async function safeLoad(label, loader, fallback) {
    try {
        return await loader();
    }
    catch (err) {
        console.error(`[Dashboard] Failed to load ${label}:`, err);
        return fallback;
    }
}
export function useDashboardOperationalSignals({ selectedDate, canViewReceiptOps, isAdmin, bookings, currentTime, }) {
    const { recentEvents } = useWebSocket();
    const mountedRef = useRef(true);
    const [pendingReceipts, setPendingReceipts] = useState([]);
    const [receiptReviewItems, setReceiptReviewItems] = useState([]);
    const [paidNotCompleted, setPaidNotCompleted] = useState([]);
    const [ocrHealth, setOcrHealth] = useState(null);
    const [lastOcrProbe, setLastOcrProbe] = useState(null);
    const [activeTimeEntries, setActiveTimeEntries] = useState([]);
    const [attentionReadIds, setAttentionReadIds] = useState(() => new Set());
    const [streamEvents, setStreamEvents] = useState([]);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);
    const appendStreamEvent = useCallback((event) => {
        const timestamp = event.timestamp ?? new Date();
        setStreamEvents((prev) => [
            { ...event, timestamp },
            ...prev.filter((existing) => existing.id !== event.id),
        ].slice(0, MAX_STREAM_EVENTS));
    }, []);
    const loadPaidNotCompleted = useCallback(async () => {
        if (!canViewReceiptOps) {
            setPaidNotCompleted([]);
            return;
        }
        const next = await safeLoad('paid bookings awaiting completion', () => getPaidNotCompletedBookings(), []);
        if (mountedRef.current)
            setPaidNotCompleted(next);
    }, [canViewReceiptOps]);
    useEffect(() => {
        loadPaidNotCompleted();
        const handle = window.setInterval(loadPaidNotCompleted, 60000);
        return () => window.clearInterval(handle);
    }, [loadPaidNotCompleted]);
    useWsEvent('booking.completed', () => { loadPaidNotCompleted(); });
    useWsEvent('booking.status_changed', () => { loadPaidNotCompleted(); });
    useWsEvent('booking.cancelled', () => { loadPaidNotCompleted(); });
    useWsEvent('invoice.paid', () => { loadPaidNotCompleted(); });
    useWsEvent('invoice.unpaid', () => { loadPaidNotCompleted(); });
    const loadSignals = useCallback(async () => {
        const [nextPendingReceipts, nextReviewItems, nextOcrHealth, nextActiveTimeEntries,] = await Promise.all([
            canViewReceiptOps
                ? safeLoad('pending receipts', () => getPendingReceipts(selectedDate), [])
                : Promise.resolve([]),
            canViewReceiptOps
                ? safeLoad('receipt reviews', async () => {
                    const [selectedDayItems, backlogItems] = await Promise.all([
                        getReceiptReviewAttentionItems(selectedDate),
                        getReceiptReviewAttentionItems(selectedDate, { backlog: true }),
                    ]);
                    return mergeReceiptReviewItems(selectedDayItems, backlogItems);
                }, [])
                : Promise.resolve([]),
            isAdmin
                ? safeLoad('OCR health', () => getOcrHealth(), {
                    reachable: false,
                    error: 'Health check failed',
                })
                : Promise.resolve(null),
            isAdmin
                ? safeLoad('active time entries', () => getActiveTimeEntries(), [])
                : Promise.resolve([]),
        ]);
        if (!mountedRef.current)
            return;
        setPendingReceipts(nextPendingReceipts);
        setReceiptReviewItems(nextReviewItems);
        setOcrHealth(nextOcrHealth);
        setActiveTimeEntries(nextActiveTimeEntries);
        if (isAdmin)
            setLastOcrProbe(new Date());
    }, [canViewReceiptOps, isAdmin, selectedDate]);
    useEffect(() => {
        loadSignals();
        const handle = window.setInterval(loadSignals, 60000);
        return () => window.clearInterval(handle);
    }, [loadSignals]);
    useEffect(() => {
        recentEvents.forEach((evt) => {
            const streamEvent = streamEventFromWs(evt);
            if (streamEvent)
                appendStreamEvent(streamEvent);
        });
    }, [appendStreamEvent, recentEvents]);
    useWsEvent('receipt.uploaded', (evt) => {
        loadSignals();
    });
    useWsEvent('receipt.deleted', (evt) => {
        loadSignals();
    });
    useWsEvent('receipt.analysis_complete', (evt) => {
        loadSignals();
    });
    useWsEvent('receipt.analysis_progress', (evt) => {
        if (['completed', 'failed', 'skipped', 'queued'].includes(evt.payload.stage)) {
            loadSignals();
        }
    });
    useWsEvent('receipt.manual_review', (evt) => {
        loadSignals();
    });
    useWsEvent('ocr.pi_health_changed', (evt) => {
        // Transition events don't carry the waiting-receipt count; keep the last one.
        setOcrHealth((prev) => ({ ...evt.payload, pendingRetries: evt.payload.pendingRetries ?? prev?.pendingRetries }));
        setLastOcrProbe(new Date(evt.timestamp));
    });
    const refreshTimeclockSignals = useCallback((evt) => {
        loadSignals();
    }, [loadSignals]);
    useWsEvent('timeclock.clocked_in', refreshTimeclockSignals);
    useWsEvent('timeclock.clocked_out', refreshTimeclockSignals);
    useWsEvent('timeclock.edited', refreshTimeclockSignals);
    const attentionItems = useMemo(() => {
        const now = currentTime.getTime();
        const venueToday = toDateStringInTz(currentTime, VENUE_TIMEZONE);
        const items = [];
        pendingReceipts.forEach((payment) => {
            items.push({
                id: `missing-receipt-${payment.paymentId}`,
                kind: 'missing_receipt',
                severity: 'amber',
                title: `${payment.booking.roomName} · ${formatCurrency(payment.amount)}`,
                detail: `${payment.booking.customerName || 'Walk-in'} · ${payment.method} · paid ${formatVenueTime(payment.createdAt)}`,
                linkHref: buildReceiptFocusHref({
                    bookingId: payment.booking.id,
                    paymentId: payment.paymentId,
                    seatIndex: payment.seatIndex,
                    status: 'MISSING_RECEIPT',
                    reason: 'Receipt missing',
                }),
                subjectId: payment.paymentId,
                createdAt: payment.createdAt,
            });
        });
        receiptReviewItems.forEach((item) => {
            const status = item.analysis?.matchStatus || item.effectiveStatus || 'NEEDS_REVIEW';
            const isBacklog = isPriorVenueDay(item.createdAt, selectedDate);
            items.push({
                id: `receipt-review-${item.paymentId}`,
                kind: 'receipt_review',
                severity: status === 'UNREADABLE' ? 'amber' : 'red',
                title: `${item.booking.roomName} · ${isBacklog ? 'OCR backlog' : 'OCR needs review'}`,
                detail: [
                    item.booking.customerName || 'Walk-in',
                    isBacklog ? `Previous day ${formatVenueDate(item.createdAt)}` : undefined,
                    status,
                    item.analysis?.mismatchReason,
                ].filter(Boolean).join(' · '),
                linkHref: buildReceiptFocusHref({
                    bookingId: item.booking.id,
                    paymentId: item.paymentId,
                    seatIndex: item.seatIndex,
                    status,
                    reason: item.analysis?.mismatchReason || undefined,
                }),
                subjectId: item.paymentId,
                createdAt: item.analysis?.analyzedAt || item.createdAt,
            });
        });
        if (isAdmin) {
            if (ocrHealth && (!ocrHealth.reachable || ocrHealth.modelLoaded === false)) {
                items.push({
                    id: 'ocr-pi-health',
                    kind: ocrHealth.reachable ? 'ocr_stuck' : 'pi_unreachable',
                    severity: ocrHealth.reachable ? 'amber' : 'red',
                    title: ocrHealth.reachable ? 'OCR model loading' : 'OCR Pi offline',
                    detail: [
                        describeOcrHealth(ocrHealth),
                        ocrHealth.pendingRetries
                            ? `${ocrHealth.pendingRetries} receipt${ocrHealth.pendingRetries === 1 ? '' : 's'} waiting — will analyze automatically`
                            : undefined,
                    ].filter(Boolean).join(' · '),
                    linkHref: '/admin/receipt-analysis',
                    createdAt: lastOcrProbe?.toISOString() || new Date().toISOString(),
                });
            }
            activeTimeEntries.forEach((entry) => {
                const clockIn = new Date(entry.clockIn);
                const clockInVenueDate = toDateStringInTz(clockIn, VENUE_TIMEZONE);
                const ageHours = (now - clockIn.getTime()) / 3600000;
                if (clockInVenueDate === venueToday && ageHours < 12)
                    return;
                items.push({
                    id: `clock-open-${entry.id}`,
                    kind: 'clock_open_overnight',
                    severity: 'red',
                    title: `${entry.employee.name} still clocked in`,
                    detail: `Open since ${formatVenueTime(entry.clockIn)}${ageHours >= 12 ? ` · ${Math.floor(ageHours)}h` : ''}`,
                    linkHref: '/pos/time-management',
                    subjectId: entry.id,
                    createdAt: entry.clockIn,
                });
            });
        }
        paidNotCompleted.forEach((booking) => {
            items.push({
                id: `paid-not-completed-${booking.id}`,
                kind: 'paid_not_completed',
                severity: 'amber',
                title: `${booking.roomName} · paid, not completed`,
                detail: [
                    booking.customerName || 'Walk-in',
                    `${formatCurrency(booking.paidTotal)} paid`,
                    isPriorVenueDay(booking.endTime, venueToday)
                        ? `ended ${formatVenueDate(booking.endTime)} ${formatVenueTime(booking.endTime)}`
                        : `ended ${formatVenueTime(booking.endTime)}`,
                ].join(' · '),
                linkHref: `/pos/booking/${booking.id}`,
                subjectId: booking.id,
                createdAt: booking.endTime,
            });
        });
        if (canViewReceiptOps) {
            bookings.forEach((booking) => {
                const status = (booking.bookingStatus || booking.status || '').toUpperCase();
                const paymentStatus = (booking.paymentStatus || '').toUpperCase();
                const isQuickSale = booking.bookingSource === 'QUICK_SALE';
                const end = new Date(booking.endTime).getTime();
                if (isQuickSale ||
                    status === 'CANCELLED' ||
                    status === 'COMPLETED' ||
                    paymentStatus === 'PAID' ||
                    Number.isNaN(end) ||
                    now - end < 15 * 60000) {
                    return;
                }
                items.push({
                    id: `unpaid-past-end-${booking.id}`,
                    kind: 'unpaid_past_end',
                    severity: 'red',
                    title: `${booking.roomName} · unpaid after session`,
                    detail: `${booking.customerName || 'Walk-in'} · ended ${formatVenueTime(booking.endTime)}`,
                    linkHref: `/pos/booking/${booking.id}`,
                    subjectId: booking.id,
                    createdAt: booking.endTime,
                });
            });
        }
        return items.sort((a, b) => {
            if (a.severity !== b.severity)
                return a.severity === 'red' ? -1 : 1;
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
    }, [
        activeTimeEntries,
        bookings,
        canViewReceiptOps,
        currentTime,
        isAdmin,
        lastOcrProbe,
        ocrHealth,
        paidNotCompleted,
        pendingReceipts,
        receiptReviewItems,
        selectedDate,
    ]);
    useEffect(() => {
        setAttentionReadIds((prev) => {
            if (prev.size === 0)
                return prev;
            const activeIds = new Set(attentionItems.map((item) => item.id));
            const next = new Set([...prev].filter((id) => activeIds.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [attentionItems]);
    const markAttentionRead = useCallback((id) => {
        setAttentionReadIds((prev) => {
            if (prev.has(id))
                return prev;
            const next = new Set(prev);
            next.add(id);
            return next;
        });
    }, []);
    return {
        attentionItems,
        attentionReadIds,
        markAttentionRead,
        pendingReceiptsCount: pendingReceipts.length,
        receiptReviewCount: receiptReviewItems.length,
        ocrHealth,
        lastOcrProbe,
        streamEvents,
    };
}
